import { ApolloError } from "@apollo/client";
import React, {
    ReactElement, useContext, useEffect, useMemo,
} from "react";
import {
    AllowedSignInMethods,
    ArtificialIntelligenceSettingsFeatures,
    Permission, PortalBannerType,
    PortalSettingsNavigation, useGetCurrentPortalQuery,
} from "../__generated__/api";
import { Loading } from "../components";
import { ErrorDefault } from "../pages/error/Error";
import useLogging from "./logging/LoggingContext";

import { useTheme } from "./Theme";

const KEY = "portal";

const ENTITLEMENT_KEYS = {
    TICKETING: "urn:fq:plan-entitlement:ticketing",
    AUDIT_LOGS: "urn:fq:plan-entitlement:audit-logs",
    ADVANCED_LOGIN: "urn:fq:plan-entitlement:advanced-signin-settings",
    ARTIFICIAL_INTELLIGENCE: "urn:fq:plan-entitlement:artificial-intelligence",
    MCP_SERVERS: "urn:fq:plan-entitlement:mcp-servers",
};

type EntitlementKeys = keyof typeof ENTITLEMENT_KEYS;
type EntitlementValues = typeof ENTITLEMENT_KEYS[EntitlementKeys];

export class Entitlements {
    public static readonly KEYS = ENTITLEMENT_KEYS;

    private entitlements: Set<EntitlementValues>;

    constructor(entitlements: ((keyof typeof Entitlements.KEYS)[] | string[] | null) = []) {
        this.entitlements = new Set(entitlements as (keyof typeof Entitlements.KEYS)[]);
    }

    // eslint-disable-next-line class-methods-use-this
    public get keys() {
        return Entitlements.KEYS;
    }

    has(entitlement: EntitlementValues): boolean {
        return this.entitlements.has(entitlement);
    }

    toArray(): EntitlementValues[] {
        return Array.from(this.entitlements);
    }
}

export interface PortalBanner {
    expirationDate: Date;
    message: string;
    linkUrl: string;
    type: PortalBannerType
}

export interface Portal {
    id?: string;
    title?: string;
    welcomeMessage?: string;
    chatToken?: string;
    lastUpdated: Date;
    indexArticleId?: string;
    permissions: Set<Permission>;
    entitlementIds: Entitlements;
    invalidate: () => void;
    clearCache: () => void;
    setTitle: (title: string) => void;
    useTitle: (title: string) => void;
    featureFlags: Set<string>;
    isAIEnabled: boolean;
    banner?: Partial<PortalBanner>;
    loginUrl?: string;
    signinMethods: Set<AllowedSignInMethods>;
    defaultSignInMethod?: AllowedSignInMethods;
    aiFeatures: ArtificialIntelligenceSettingsFeatures;
    navigation?: PortalSettingsNavigation;
}

interface PortalSerialized {
    title?: string;
    id?: string;
    welcomeMessage?: string;
    chatToken?: string;
    indexArticleId?: string;
    lastUpdated: number;
    permissions: Permission[];
    entitlementIds: string[];
    featureFlags: string[];
    isAIEnabled: boolean;
    trialExpiration: string;
    expirationReminderDate: string;
    banner?: Partial<PortalBanner>
    loginUrl?: string;
    signinMethods: AllowedSignInMethods[];
    defaultSignInMethod?: AllowedSignInMethods;
    aiFeatures?: ArtificialIntelligenceSettingsFeatures;
    navigation?: PortalSettingsNavigation;
}

const DEFAULT_SIGNIN_METHODS = new Set([
    AllowedSignInMethods.Google,
    AllowedSignInMethods.SingleSignOn,
    AllowedSignInMethods.Slack,
]);

export const PortalContext = React.createContext<Portal>({
    lastUpdated: new Date(),
    permissions: new Set(),
    entitlementIds: new Entitlements(),
    invalidate: () => { /* do nothing */ },
    clearCache: () => { /* do nothing */ },
    setTitle: () => { /* do nothing */ },
    useTitle: () => { /* do nothing */ },
    featureFlags: new Set(),
    isAIEnabled: false,
    signinMethods: new Set([AllowedSignInMethods.Google, AllowedSignInMethods.SingleSignOn, AllowedSignInMethods.Slack]),
    aiFeatures: Object.create(null),
    navigation: Object.create(null),
});

export function PortalProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const {
        loading, data, error, refetch,
    } = useGetCurrentPortalQuery();

    const theme = useTheme();

    const logging = useLogging();

    const clearCache = () => {
        localStorage.removeItem(KEY);
    };

    const invalidate = () => {
        clearCache();
        refetch();
    };

    const setTitle = (portal: Portal | undefined, title?: string) => {
        const full = ["Foqal"];
        if (portal?.title) {
            full.push(portal.title);
        }
        if (title) {
            full.push(title);
        }
        document.title = full.join(" | ");
    };

    const useTitle = (portal: Portal | undefined, title?: string) => {
        useEffect(() => {
            setTitle(portal, title);
            return () => {
                setTitle(portal, "");
            };
        });
    };

    const portal: Portal | undefined = useMemo(() => {
        let current: Portal | undefined;
        if (data?.portal) {
            current = {
                id: data.portal.id,
                chatToken: data.portal.chatToken as string,
                title: data.portal.title as string,
                welcomeMessage: data.portal.welcomeMessage as string,
                permissions: new Set(data.portal.permissions),
                entitlementIds: new Entitlements(data.portal.entitlementIds),
                lastUpdated: new Date(),
                indexArticleId: data.portal.indexArticleId,
                invalidate,
                clearCache,
                setTitle: (title: string) => setTitle(current, title),
                useTitle: (title: string) => useTitle(current, title),
                featureFlags: new Set(data.featureFlags?.active ?? []),
                isAIEnabled: data.portal.isAIEnabled as boolean,
                banner: {
                    ...data.portal.banner,
                    expirationDate: data.portal.banner?.expirationDate
                        ? new Date(data.portal.banner?.expirationDate)
                        : undefined,
                } as PortalBanner,
                loginUrl: data.portal.singleSignOn?.isEnabled ? data.portal.singleSignOn?.loginRedirectUrl as string : undefined,
                signinMethods: data.portal.signIn?.allowedSigninMethods
                    ? new Set(data.portal.signIn?.allowedSigninMethods)
                    : DEFAULT_SIGNIN_METHODS,
                defaultSignInMethod: data.portal.signIn?.defaultSignInMethod as AllowedSignInMethods | undefined,
                aiFeatures: data.portal.aiFeatures as ArtificialIntelligenceSettingsFeatures ?? {},
                navigation: data.portal.navigation as PortalSettingsNavigation,
            };
        }

        if (current) {
            localStorage.setItem(KEY, JSON.stringify({
                ...current,
                lastUpdated: current.lastUpdated.getTime(),
                permissions: Array.from(current.permissions),
                entitlementIds: current.entitlementIds?.toArray(),
                featureFlags: Array.from(current.featureFlags),
                signinMethods: Array.from(current.signinMethods),
                aiFeatures: current.aiFeatures ?? {},
                navigation: current.navigation,
            }));
        } else {
            const cachedPortal = localStorage.getItem(KEY);
            if (cachedPortal) {
                const localPortal = JSON.parse(cachedPortal) as PortalSerialized;
                current = {
                    id: localPortal.id,
                    lastUpdated: new Date(localPortal.lastUpdated),
                    permissions: new Set(localPortal.permissions),
                    entitlementIds: new Entitlements(localPortal.entitlementIds),
                    chatToken: localPortal.chatToken,
                    title: localPortal.title,
                    welcomeMessage: localPortal.welcomeMessage,
                    indexArticleId: localPortal.indexArticleId,
                    invalidate,
                    clearCache,
                    setTitle: (title: string) => setTitle(current, title),
                    useTitle: (title: string) => useTitle(current, title),
                    isAIEnabled: localPortal.isAIEnabled,
                    featureFlags: new Set(data?.featureFlags?.active ?? localPortal.featureFlags),
                    banner: {
                        ...localPortal.banner,
                        expirationDate: localPortal.banner?.expirationDate
                            ? new Date(localPortal.banner?.expirationDate)
                            : undefined,
                    },
                    loginUrl: localPortal.loginUrl,
                    signinMethods: new Set(localPortal.signinMethods),
                    defaultSignInMethod: localPortal.defaultSignInMethod,
                    aiFeatures: localPortal.aiFeatures as ArtificialIntelligenceSettingsFeatures ?? {},
                    navigation: localPortal.navigation as PortalSettingsNavigation,
                };
            }
        }

        return current;
    }, [data?.portal]);

    useEffect(() => {
        if (data?.portal) {
            logging.info({
                portalId: data.portal.id,
                permissions: Array.from(data.portal.permissions),
                entitlementIds: Array.from(data.portal.entitlementIds ?? []),
                featureFlags: Array.from(data.featureFlags?.active ?? []),
            }, "Portal Started");

            theme.setFeatureFlags(new Set(data.featureFlags?.active));
            setTitle(portal);
        }
    }, [data?.portal?.id]);

    if (error) {
        return <ErrorDefault message={error instanceof ApolloError ? error.message : "Unknown error occured."} />;
    }
    if (loading) {
        return <Loading />;
    }

    if (!portal) {
        return <Loading />;
    }

    return (
        <PortalContext.Provider value={portal}>
            {children}
        </PortalContext.Provider>
    );
}

export default function usePortal(): Portal {
    return useContext(PortalContext) ?? {};
}
